import { useState, useCallback, useRef } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Slider } from './slider';
import { Switch } from './switch';
import { 
  Upload, X, Check, Image as ImageIcon, Loader2, Plus, GripVertical,
  Droplet, Type, MapPin, ZoomIn, ZoomOut, Trash2, Download, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './tabs';

const API = process.env.REACT_APP_BACKEND_URL;

// Watermark position options
const watermarkPositions = [
  { value: 'top-left', label: 'Top Left' },
  { value: 'top-right', label: 'Top Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'bottom-right', label: 'Bottom Right' },
  { value: 'center', label: 'Center' },
];

export const AdvancedImageUploader = ({
  images = [],
  onImagesChange,
  label = "Upload Images",
  maxImages = 20,
  showWatermark = true,
  defaultWatermarkText = "Raj Aluminium",
  defaultContact = "",
  className = "",
  token
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewModal, setPreviewModal] = useState({ open: false, images: [], currentIndex: 0 });
  const [watermarkSettings, setWatermarkSettings] = useState({
    enabled: false,
    text: defaultWatermarkText,
    contact: defaultContact,
    position: 'bottom-right',
    opacity: 40
  });
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    
    if (files.length > 0) {
      processFiles(files);
    }
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      processFiles(files);
    }
    e.target.value = '';
  };

  const processFiles = async (files) => {
    // Check max limit
    if (images.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed. You can add ${maxImages - images.length} more.`);
      files = files.slice(0, maxImages - images.length);
    }

    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      
      // Add all files
      files.forEach(file => {
        formData.append('files', file);
      });
      
      // Add watermark settings
      formData.append('add_watermark', watermarkSettings.enabled.toString());
      if (watermarkSettings.enabled) {
        formData.append('watermark_text', watermarkSettings.text);
        formData.append('watermark_contact', watermarkSettings.contact);
        formData.append('watermark_position', watermarkSettings.position);
        formData.append('watermark_opacity', watermarkSettings.opacity.toString());
      }
      formData.append('max_width', '1920');
      formData.append('quality', '85');

      const response = await fetch(`${API}/api/admin/upload-batch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const result = await response.json();
      
      // Add uploaded images to the list
      const newImages = result.uploaded.map(img => ({
        url: img.url,
        thumbnail: img.thumbnail_url,
        filename: img.filename,
        originalName: img.original_name,
        size: img.final_size
      }));

      onImagesChange([...images, ...newImages]);
      
      if (result.error_count > 0) {
        toast.warning(`Uploaded ${result.success_count} images. ${result.error_count} failed.`);
      } else {
        toast.success(`Successfully uploaded ${result.success_count} images!`);
      }

    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload images');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };

  const moveImage = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= images.length) return;
    const newImages = [...images];
    const [moved] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, moved);
    onImagesChange(newImages);
  };

  const openPreview = (index) => {
    setPreviewModal({ open: true, images, currentIndex: index });
  };

  return (
    <div className={`space-y-4 ${className}`} data-testid="advanced-image-uploader">
      <Label className="text-base font-semibold flex items-center gap-2">
        <ImageIcon className="w-5 h-5" /> {label}
      </Label>

      {/* Watermark Settings */}
      {showWatermark && (
        <div className="p-4 bg-slate-50 rounded-lg border space-y-4">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2 font-medium">
              <Type className="w-4 h-4" /> Watermark Settings
            </Label>
            <div className="flex items-center gap-2">
              <Switch 
                checked={watermarkSettings.enabled}
                onCheckedChange={(enabled) => setWatermarkSettings(prev => ({ ...prev, enabled }))}
                data-testid="watermark-toggle"
              />
              <span className="text-sm text-slate-600">
                {watermarkSettings.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>

          {watermarkSettings.enabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
              <div className="space-y-2">
                <Label className="text-sm">Watermark Text</Label>
                <Input
                  value={watermarkSettings.text}
                  onChange={(e) => setWatermarkSettings(prev => ({ ...prev, text: e.target.value }))}
                  placeholder="Raj Aluminium"
                  data-testid="watermark-text"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm">Contact Number</Label>
                <Input
                  value={watermarkSettings.contact}
                  onChange={(e) => setWatermarkSettings(prev => ({ ...prev, contact: e.target.value }))}
                  placeholder="+91 98765 43210"
                  data-testid="watermark-contact"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Position
                </Label>
                <Select 
                  value={watermarkSettings.position}
                  onValueChange={(position) => setWatermarkSettings(prev => ({ ...prev, position }))}
                >
                  <SelectTrigger data-testid="watermark-position">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {watermarkPositions.map(pos => (
                      <SelectItem key={pos.value} value={pos.value}>
                        {pos.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm flex items-center gap-1">
                  <Droplet className="w-3 h-3" /> Opacity: {watermarkSettings.opacity}%
                </Label>
                <Slider
                  value={[watermarkSettings.opacity]}
                  onValueChange={([opacity]) => setWatermarkSettings(prev => ({ ...prev, opacity }))}
                  min={10}
                  max={100}
                  step={5}
                  className="w-full"
                  data-testid="watermark-opacity"
                />
              </div>
            </div>
          )}

          {watermarkSettings.enabled && (
            <p className="text-xs text-slate-500 bg-sky-50 p-2 rounded">
              Preview: <strong>"{watermarkSettings.text}{watermarkSettings.contact ? ` | Contact: ${watermarkSettings.contact}` : ''}"</strong> 
              <span className="ml-2">({watermarkSettings.position}, {watermarkSettings.opacity}% opacity)</span>
            </p>
          )}
        </div>
      )}

      {/* Uploaded Images Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
          {images.map((img, index) => (
            <div 
              key={index} 
              className="relative group aspect-square rounded-lg overflow-hidden border-2 border-slate-200 bg-slate-50 hover:border-sky-400 transition-colors"
              data-testid={`uploaded-image-${index}`}
            >
              <img 
                src={img.thumbnail || img.url} 
                alt={`Image ${index + 1}`} 
                className="w-full h-full object-cover cursor-pointer"
                onClick={() => openPreview(index)}
                loading="lazy"
              />
              
              {/* Overlay actions */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="w-7 h-7"
                  onClick={() => openPreview(index)}
                  title="Preview"
                >
                  <Eye className="w-4 h-4" />
                </Button>
                {index > 0 && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className="w-7 h-7"
                    onClick={() => moveImage(index, index - 1)}
                    title="Move left"
                  >
                    <GripVertical className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="w-7 h-7"
                  onClick={() => removeImage(index)}
                  title="Remove"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              {/* First image badge */}
              {index === 0 && (
                <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-sky-500 text-white text-[10px] font-medium rounded">
                  Main
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
          isDragging 
            ? 'border-sky-500 bg-sky-50 scale-[1.02]' 
            : 'border-slate-300 hover:border-sky-400 hover:bg-sky-50/50'
        } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
        data-testid="upload-dropzone"
      >
        {uploading ? (
          <div className="space-y-3">
            <Loader2 className="w-12 h-12 text-sky-500 mx-auto animate-spin" />
            <p className="text-slate-600 font-medium">Uploading images...</p>
            <div className="w-48 h-2 bg-slate-200 rounded-full mx-auto overflow-hidden">
              <div 
                className="h-full bg-sky-500 transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        ) : (
          <>
            <Upload className={`w-12 h-12 mx-auto mb-3 ${isDragging ? 'text-sky-500' : 'text-slate-400'}`} />
            <p className="text-slate-700 font-medium mb-1">
              {isDragging ? 'Drop images here!' : 'Drag & drop images here'}
            </p>
            <p className="text-sm text-slate-500 mb-3">or click to browse</p>
            <p className="text-xs text-slate-400">
              Supports: JPG, PNG, WEBP • Max 10MB per file • {images.length}/{maxImages} uploaded
            </p>
            {watermarkSettings.enabled && (
              <p className="text-xs text-sky-600 mt-2 font-medium">
                ✓ Watermark will be added automatically
              </p>
            )}
          </>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        data-testid="file-input"
      />

      {/* Image Preview Modal */}
      <ImagePreviewModal 
        open={previewModal.open}
        images={previewModal.images}
        initialIndex={previewModal.currentIndex}
        onClose={() => setPreviewModal({ open: false, images: [], currentIndex: 0 })}
      />
    </div>
  );
};

// Image Preview Modal with Gallery Features
const ImagePreviewModal = ({ open, images, initialIndex = 0, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  // Reset state when modal opens
  useState(() => {
    if (open) {
      setCurrentIndex(initialIndex);
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [open, initialIndex]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.5, 3));
  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.5, 1));
    if (zoom <= 1.5) setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  // Touch handlers for mobile swipe
  const [touchStart, setTouchStart] = useState(null);
  
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) handleNext();
      else handlePrev();
    }
    
    setTouchStart(null);
  };

  // Keyboard navigation
  useState(() => {
    const handleKeyDown = (e) => {
      if (!open) return;
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape') onClose();
      if (e.key === '+' || e.key === '=') handleZoomIn();
      if (e.key === '-') handleZoomOut();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  if (!images.length) return null;
  const currentImage = images[currentIndex];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-5xl h-[90vh] p-0 bg-black/95 border-0">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <span className="text-white/70 text-sm">
              {currentIndex + 1} / {images.length}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20"
                onClick={handleZoomOut}
                disabled={zoom <= 1}
              >
                <ZoomOut className="w-5 h-5" />
              </Button>
              <span className="text-white/70 text-sm w-12 text-center">{Math.round(zoom * 100)}%</span>
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20"
                onClick={handleZoomIn}
                disabled={zoom >= 3}
              >
                <ZoomIn className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20 ml-4"
                onClick={onClose}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Main Image */}
          <div 
            ref={containerRef}
            className="flex-1 relative overflow-hidden cursor-grab active:cursor-grabbing"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div 
              className="absolute inset-0 flex items-center justify-center transition-transform duration-200"
              style={{
                transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`
              }}
            >
              <img
                src={currentImage?.url}
                alt={`Image ${currentIndex + 1}`}
                className="max-w-full max-h-full object-contain select-none"
                draggable={false}
              />
            </div>

            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full"
                  onClick={handlePrev}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full"
                  onClick={handleNext}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="p-4 border-t border-white/10">
              <div className="flex gap-2 overflow-x-auto pb-2 justify-center">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setCurrentIndex(idx);
                      setZoom(1);
                      setPosition({ x: 0, y: 0 });
                    }}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === currentIndex 
                        ? 'border-sky-500 scale-105' 
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={img.thumbnail || img.url}
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdvancedImageUploader;
